import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, FileText, Loader2, AlertCircle, Check, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  en: {
    review: 'Property Evaluation', subtitle: 'Property Condition Assessment & Recommendations',
    recordNo: 'Property Evaluation No.', purpose: 'Inspection purpose',
    intro: 'Total Facility Solutions completed a property evaluation and identified opportunities for maintenance, correction, or improvement. Please review each item below and let us know how you would like to proceed.',
    evaluation: 'Evaluation', date: 'Inspection date', existing: 'Existing condition', recommended: 'Recommended action', priority: 'Priority',
    choose: 'How would you like TFS to proceed with this item?',
    enlarge: 'Click or tap image to enlarge', proposedConcept: 'Proposed Concept — Illustration Only',
    proceed: 'Please Proceed', proposal: 'Request Proposal', other: 'Other Service Provider', defer: 'Defer / No Action', pending: 'Decision Pending',
    fixedPrice: 'Fixed Price', nte: 'My NTE', proposalRequired: 'Pricing: Proposal Required',
    myNte: 'My Maximum Authorized Amount (NTE)', ntePlaceholder: 'Enter amount (optional)',
    nteHelp: 'Optional. This is the most you authorize for this scope — it does not change the TFS Fixed Price, and a higher amount does not authorize extra work.',
    nteSummaryTitle: 'Your maximum authorized amounts (NTE)', reviewTag: 'Authorization review',
    nteSummaryNote: 'NTE amounts are maximum spending limits you set, not automatic charges. Items marked for review will be confirmed by TFS management before any work proceeds.',
    comment: 'Optional comment', yourName: 'Your name', yourEmail: 'Your email',
    submit: 'Submit my decisions', submitting: 'Submitting…',
    reviewed: 'Recommendations Reviewed', recorded: 'Recorded', recordedNote: 'Already submitted — contact TFS to change this decision.',
    confirmTitle: 'Submit your decisions?', reviewRemaining: 'Review remaining item', submitN: (n) => `Submit ${n} decision${n === 1 ? '' : 's'}`,
    unansweredMsg: (a, tot, rem) => `You have reviewed ${a} of ${tot} recommendations. ${rem} item${rem === 1 ? '' : 's'} remain${rem === 1 ? 's' : ''} unanswered. You may submit your ${a} decision${a === 1 ? '' : 's'} now and return to the remaining item${rem === 1 ? '' : 's'} later using this same link.`,
    authTitle: 'You are authorizing the following work', fixedSubtotal: 'Fixed-Price Subtotal', nteCeiling: 'Additional NTE Authorization Ceiling',
    authNote: 'Fixed prices are defined charges. NTE amounts are maximum spending authorizations, not automatic charges.',
    confirmAuthorize: 'Confirm & Submit', goBack: 'Go back',
    done: 'Thank you — your decisions have been received.', doneSub: 'TFS will follow up on the items you selected. You may close this page.',
    returnNote: 'You can return to the remaining recommendations using the original email link.',
    sumSubmitted: 'Decisions submitted', sumAuthorized: 'Work authorized', sumProposal: 'Proposal requests', sumOther: 'Other provider', sumDeferred: 'Deferred / pending', sumRemaining: 'Still to review',
    already: 'Your response has already been submitted. Thank you.',
    expired: 'This review link is no longer valid. Please contact TFS to request a new link.', none: 'No items to review.',
  },
  es: {
    review: 'Evaluación de la Propiedad', subtitle: 'Evaluación de Condiciones y Recomendaciones de la Propiedad',
    recordNo: 'N.º de Evaluación de la Propiedad', purpose: 'Propósito de la inspección',
    intro: 'Total Facility Solutions completó una evaluación de la propiedad e identificó oportunidades de mantenimiento, corrección o mejora. Revise cada punto a continuación e indíquenos cómo desea proceder.',
    evaluation: 'Evaluación', date: 'Fecha de inspección', existing: 'Condición existente', recommended: 'Acción recomendada', priority: 'Prioridad',
    choose: '¿Cómo desea que TFS proceda con este asunto?',
    enlarge: 'Haga clic o toque la imagen para ampliar', proposedConcept: 'Concepto propuesto — Solo ilustración',
    proceed: 'Proceder', proposal: 'Solicitar Cotización', other: 'Otro Proveedor', defer: 'Aplazar / Sin Acción', pending: 'Decisión Pendiente',
    fixedPrice: 'Precio Fijo', nte: 'Mi NTE', proposalRequired: 'Precio: Cotización Requerida',
    myNte: 'Mi monto máximo autorizado (NTE)', ntePlaceholder: 'Ingrese un monto (opcional)',
    nteHelp: 'Opcional. Es lo máximo que usted autoriza para este alcance — no cambia el Precio Fijo de TFS, y un monto mayor no autoriza trabajo adicional.',
    nteSummaryTitle: 'Sus montos máximos autorizados (NTE)', reviewTag: 'Revisión de autorización',
    nteSummaryNote: 'Los montos NTE son límites máximos de gasto que usted establece, no cargos automáticos. Los puntos marcados para revisión serán confirmados por la gerencia de TFS antes de iniciar cualquier trabajo.',
    comment: 'Comentario opcional', yourName: 'Su nombre', yourEmail: 'Su correo',
    submit: 'Enviar mis decisiones', submitting: 'Enviando…',
    reviewed: 'Recomendaciones revisadas', recorded: 'Registrado', recordedNote: 'Ya enviado — contacte a TFS para cambiar esta decisión.',
    confirmTitle: '¿Enviar sus decisiones?', reviewRemaining: 'Revisar punto restante', submitN: (n) => `Enviar ${n} decisión${n === 1 ? '' : 'es'}`,
    unansweredMsg: (a, tot, rem) => `Ha revisado ${a} de ${tot} recomendaciones. Queda${rem === 1 ? '' : 'n'} ${rem} punto${rem === 1 ? '' : 's'} sin responder. Puede enviar sus ${a} decisión${a === 1 ? '' : 'es'} ahora y volver a los puntos restantes más tarde con este mismo enlace.`,
    authTitle: 'Usted está autorizando el siguiente trabajo', fixedSubtotal: 'Subtotal de precio fijo', nteCeiling: 'Límite adicional de autorización NTE',
    authNote: 'Los precios fijos son cargos definidos. Los montos NTE son autorizaciones máximas de gasto, no cargos automáticos.',
    confirmAuthorize: 'Confirmar y enviar', goBack: 'Volver',
    done: 'Gracias — hemos recibido sus decisiones.', doneSub: 'TFS dará seguimiento a los puntos seleccionados. Puede cerrar esta página.',
    returnNote: 'Puede volver a las recomendaciones restantes usando el enlace del correo original.',
    sumSubmitted: 'Decisiones enviadas', sumAuthorized: 'Trabajo autorizado', sumProposal: 'Cotizaciones solicitadas', sumOther: 'Otro proveedor', sumDeferred: 'Aplazadas / pendientes', sumRemaining: 'Por revisar',
    already: 'Su respuesta ya ha sido enviada. Gracias.',
    expired: 'Este enlace de revisión ya no es válido. Comuníquese con TFS para solicitar uno nuevo.', none: 'No hay puntos para revisar.',
  },
};

const OPTIONS = [
  { key: 'please_proceed', tKey: 'proceed', cls: 'border-[#8FC93A] bg-[#8FC93A]/15 text-[#3f6212]' },
  { key: 'request_proposal', tKey: 'proposal', cls: 'border-[#0E2A47] bg-[#0E2A47]/10 text-[#0E2A47]' },
  { key: 'other_provider', tKey: 'other', cls: 'border-slate-400 bg-slate-100 text-slate-700' },
  { key: 'defer', tKey: 'defer', cls: 'border-amber-400 bg-amber-50 text-amber-700' },
  { key: 'decision_pending', tKey: 'pending', cls: 'border-indigo-400 bg-indigo-50 text-indigo-700' },
];

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const OPT_LABEL = { please_proceed: 'proceed', request_proposal: 'proposal', other_provider: 'other', defer: 'defer', decision_pending: 'pending' };

// MM/DD/YY — tz-safe (reads the date portion directly, no Date() drift).
const fmtDate = (s) => {
  if (!s) return '';
  const m = String(s).slice(0, 10).match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : String(s).slice(0, 10);
};

export default function PevCustomerReview() {
  const { iid } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token');
  const [lang, setLang] = useState('en');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [choices, setChoices] = useState({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneInfo, setDoneInfo] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { photos, index }
  const t = T[lang];

  const photoSrc = (p) => `${API}/operations/inspections/${iid}/photo/${p.id}?auth=${token}`;

  const load = async (lg) => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/operations/inspections/${iid}/public-review`, { params: { token, lang: lg } });
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || t.expired); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(lang); /* eslint-disable-next-line */ }, [lang]);

  const committed = data?.decisions || {};              // server-recorded (locked) decisions
  const findings = data?.findings || [];
  const isLocked = (fid) => !!committed[fid];
  const newSelections = useMemo(() => Object.entries(choices)
    .filter(([fid, v]) => v?.decision && !isLocked(fid)), [choices, committed]);
  const committedCount = Object.keys(committed).length;
  const reviewedCount = committedCount + newSelections.length;
  const unanswered = findings.length - reviewedCount;

  const setDecision = (fid, decision) => { if (!isLocked(fid)) setChoices((c) => ({ ...c, [fid]: { ...(c[fid] || {}), decision } })); };
  const setComment = (fid, comment) => { if (!isLocked(fid)) setChoices((c) => ({ ...c, [fid]: { ...(c[fid] || {}), comment } })); };
  const setNte = (fid, nte) => { if (!isLocked(fid)) setChoices((c) => ({ ...c, [fid]: { ...(c[fid] || {}), nte } })); };

  // Fixed-price subtotal (authorized) + customer-defined NTE lines (separate, may need review).
  const authSummary = useMemo(() => {
    let fixed = 0; const fixedLines = [], nteLines = [];
    for (const [fid, v] of newSelections) {
      if (v.decision !== 'please_proceed') continue;
      const f = findings.find((x) => x.id === fid); const pr = f?.pricing;
      const fixedAmt = pr && pr.type === 'fixed' ? Number(pr.amount || 0) : null;
      const nte = v.nte != null && v.nte !== '' && Number(v.nte) > 0 ? Number(v.nte) : null;
      const review = (fixedAmt && nte != null && nte < fixedAmt) || (!fixedAmt && nte != null) || (!fixedAmt);
      if (fixedAmt && !(nte != null && nte < fixedAmt)) { fixed += fixedAmt; fixedLines.push({ title: f.title, amount: fixedAmt }); }
      if (nte != null) nteLines.push({ title: f.title, amount: nte, review });
      else if (!fixedAmt) nteLines.push({ title: f.title, amount: null, review: true });
    }
    return { fixed, fixedLines, nteLines };
  }, [newSelections, findings]);

  const lbNav = (dir) => setLightbox((lb) => {
    if (!lb) return lb;
    const n = lb.photos.length;
    return { ...lb, index: (lb.index + dir + n) % n };
  });
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowLeft') lbNav(-1);
      else if (e.key === 'ArrowRight') lbNav(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const onSubmitClick = () => { if (newSelections.length === 0) return; setConfirmOpen(true); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const decisions = newSelections.map(([finding_id, v]) => ({ finding_id, decision: v.decision, comment: v.comment || null, nte: v.nte != null && v.nte !== '' && Number(v.nte) > 0 ? Number(v.nte) : null }));
      const r = await axios.post(`${API}/operations/inspections/${iid}/public-review/submit`, { decisions, contact_name: name, contact_email: email, lang }, { params: { token } });
      setDoneInfo(r.data);
      setConfirmOpen(false);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setErr(e?.response?.data?.detail || 'Submission failed. Please retry.'); }
    finally { setSubmitting(false); }
  };

  const LangToggle = () => (
    <div className="flex gap-1 rounded-lg border border-[#0E2A47]/20 bg-slate-50 p-0.5" data-testid="pev-review-lang">
      {['en', 'es'].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`pev-review-lang-${l}`}
          className={`rounded-md px-3 py-1 text-xs font-bold transition ${lang === l ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`}>{l.toUpperCase()}</button>
      ))}
    </div>
  );

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-[#0E2A47]" /></div>;
  if (err && !data) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow" data-testid="pev-review-error">
        <AlertCircle className="mx-auto mb-3 text-amber-500" size={40} />
        <p className="text-slate-700">{err}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white px-4 py-2.5 shadow-sm" data-testid="pev-review-header">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <img src="/tfs_report_logo.png" alt="Total Facility Solutions, Inc. — facility forward" className="h-9 w-auto sm:h-11" data-testid="pev-review-tfs-logo" />
          <LangToggle />
        </div>
      </header>

      {done ? (
        <div className="mx-auto mt-10 max-w-md rounded-2xl bg-white p-8 text-center shadow" data-testid="pev-review-confirmation">
          <CheckCircle2 className="mx-auto mb-3 text-[#8FC93A]" size={48} />
          <p className="text-lg font-bold text-[#0E2A47]">{t.done}</p>
          <p className="mt-2 text-sm text-slate-500">{t.doneSub}</p>
          {doneInfo && (
            <div className="mt-4 space-y-1.5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left text-sm" data-testid="pev-review-confirmation-summary">
              <div className="flex justify-between"><span className="text-slate-500">{t.sumSubmitted}</span><b className="text-[#0E2A47]">{doneInfo.submitted}</b></div>
              {doneInfo.work_orders_created > 0 && <div className="flex justify-between"><span className="text-slate-500">{t.sumAuthorized}</span><b className="text-[#3f6212]">{doneInfo.work_orders_created}</b></div>}
              {doneInfo.proposals > 0 && <div className="flex justify-between"><span className="text-slate-500">{t.sumProposal}</span><b className="text-[#0E2A47]">{doneInfo.proposals}</b></div>}
              {doneInfo.unanswered > 0 && <div className="flex justify-between"><span className="text-slate-500">{t.sumRemaining}</span><b className="text-amber-600">{doneInfo.unanswered}</b></div>}
            </div>
          )}
          {doneInfo?.unanswered > 0 && <p className="mt-3 text-xs text-slate-400">{t.returnNote}</p>}
        </div>
      ) : (
        <main className="mx-auto max-w-3xl px-4 py-5">
          <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-6" data-testid="pev-review-identity">
            <h1 className="text-2xl font-extrabold tracking-tight text-[#0E2A47] sm:text-3xl">{t.review}</h1>
            <p className="mt-1 text-sm font-medium text-slate-500 sm:text-base">{t.subtitle}</p>
            <div className="mt-3 h-1 w-16 rounded-full bg-[#8FC93A]" />

            <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                {data?.has_customer_logo && (
                  <img src={`${API}/operations/inspections/${iid}/public-review/customer-logo?token=${token}`} alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="h-12 w-12 shrink-0 rounded-lg border border-slate-200 bg-white object-contain p-1 sm:h-14 sm:w-14"
                    data-testid="pev-review-customer-logo" />
                )}
                <div className="min-w-0">
                  <div className="truncate text-base font-bold text-[#0E2A47]" data-testid="pev-review-customer-name">{data?.customer_name}</div>
                  {data?.location_name && <div className="truncate text-sm font-medium text-slate-600" data-testid="pev-review-location-name">{data.location_name}</div>}
                  {data?.location_address && <div className="text-xs text-slate-400" data-testid="pev-review-location-address">{data.location_address}</div>}
                </div>
              </div>
              {data?.has_location_photo && (
                <img src={`${API}/operations/inspections/${iid}/public-review/location-photo?token=${token}`} alt=""
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  className="h-24 w-full shrink-0 rounded-xl border border-slate-200 object-cover sm:h-20 sm:w-32"
                  data-testid="pev-review-location-photo" />
              )}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-3" data-testid="pev-review-meta">
              <div className="bg-slate-50 px-4 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t.recordNo}</div>
                <div className="mt-0.5 text-sm font-bold text-[#0E2A47]">{data?.inspection_number || '—'}</div>
              </div>
              <div className="bg-slate-50 px-4 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t.purpose}</div>
                <div className="mt-0.5 text-sm font-bold text-[#0E2A47]">{data?.purpose_label || '—'}</div>
              </div>
              <div className="bg-slate-50 px-4 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{t.date}</div>
                <div className="mt-0.5 text-sm font-bold text-[#0E2A47]">{fmtDate(data?.inspection_date) || '—'}</div>
              </div>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-slate-600">{t.intro}</p>
          </div>

          {(data?.findings || []).length === 0 && <p className="mt-6 text-center text-sm text-slate-400">{t.none}</p>}

          {(data?.findings || []).map((f, i) => {
            const locked = isLocked(f.id);
            const lockedDecision = committed[f.id]?.decision;
            const pr = f.pricing;
            return (
            <section key={f.id} className={`mt-4 rounded-2xl p-5 shadow-sm ${locked ? 'border border-[#8FC93A]/40 bg-[#8FC93A]/5' : 'bg-white'}`} data-testid={`pev-review-finding-${f.id}`}>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0E2A47] text-xs font-bold text-white">{i + 1}</span>
                <h2 className="text-base font-bold text-[#0E2A47]">{f.title}</h2>
              </div>
              {f.existing_condition && <p className="mt-2 text-sm text-slate-700"><b>{t.existing}:</b> {f.existing_condition}</p>}
              {f.recommended_action && <p className="mt-1 text-sm text-slate-700"><b>{t.recommended}:</b> {f.recommended_action}</p>}
              {f.priority && <p className="mt-1 text-xs text-slate-400">{t.priority}: {f.priority}</p>}
              {f.photos?.length > 0 && (
                <div className="mt-3">
                  <div className={`grid gap-3 ${f.photos.length > 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
                    {f.photos.map((p, pi) => (
                      <figure key={p.id} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                        <button type="button" onClick={() => setLightbox({ photos: f.photos, index: pi })}
                          data-testid={`pev-review-photo-${p.id}`}
                          className="block w-full cursor-zoom-in">
                          <img src={photoSrc(p)} alt={p.caption || ''} loading="lazy"
                            className="max-h-[420px] w-full object-contain" />
                          <span className="pointer-events-none absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition group-hover:opacity-100"><ZoomIn size={16} /></span>
                        </button>
                        {p.kind === 'proposed' && (
                          <figcaption className="absolute bottom-0 left-0 right-0 bg-[#8FC93A] px-2 py-1 text-center text-[10px] font-bold uppercase tracking-wide text-[#0E2A47]">{t.proposedConcept}</figcaption>
                        )}
                        {p.kind !== 'proposed' && p.caption && (
                          <figcaption className="absolute bottom-0 left-0 right-0 bg-black/55 px-2 py-1 text-center text-[11px] text-white">{p.caption}</figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                  <p className="mt-2 text-center text-xs font-medium text-slate-400" data-testid={`pev-review-enlarge-${f.id}`}>{t.enlarge}</p>
                </div>
              )}
              {locked ? (
                <div className="mt-4 rounded-xl border border-[#8FC93A]/50 bg-white p-3" data-testid={`pev-review-locked-${f.id}`}>
                  <div className="flex items-center gap-2 text-sm font-bold text-[#3f6212]">
                    <CheckCircle2 size={16} /> {t.recorded}: {t[OPT_LABEL[lockedDecision]] || lockedDecision}
                  </div>
                  <p className="mt-1 text-xs text-slate-400">{t.recordedNote}</p>
                </div>
              ) : (
              <div className="mt-4">
                {pr && (pr.type === 'fixed' || pr.type === 'nte') && (
                  <div className="mb-3 flex sm:justify-end" data-testid={`pev-review-price-${f.id}`}>
                    <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#8FC93A] px-4 py-2 text-base font-bold text-white shadow-sm">
                      {t.fixedPrice}: {money(pr.amount)}
                    </div>
                  </div>
                )}
                {pr && pr.type === 'proposal' && (
                  <div className="mb-3 flex sm:justify-end" data-testid={`pev-review-price-${f.id}`}>
                    <div className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-4 py-2 text-sm font-bold text-white">{t.proposalRequired}</div>
                  </div>
                )}
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t.choose}</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                  {OPTIONS.map((o) => {
                    const active = choices[f.id]?.decision === o.key;
                    return (
                      <button key={o.key} onClick={() => setDecision(f.id, o.key)} data-testid={`pev-review-opt-${f.id}-${o.key}`}
                        className={`relative flex min-h-[52px] items-center justify-center rounded-xl border-2 px-2 py-2 text-xs font-bold transition ${active ? `${o.cls} shadow-sm` : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'}`}>
                        {active && <Check size={14} className="mr-1 shrink-0" strokeWidth={3} />}
                        <span>{t[o.tKey]}</span>
                      </button>
                    );
                  })}
                </div>
                {choices[f.id]?.decision === 'please_proceed' && (
                  <div className="mt-3 rounded-xl border border-[#0E2A47]/15 bg-slate-50 p-3" data-testid={`pev-review-nte-wrap-${f.id}`}>
                    <label className="text-xs font-semibold text-[#0E2A47]">{t.myNte}</label>
                    <div className="mt-1.5 flex items-center rounded-lg border border-slate-300 bg-white px-3">
                      <span className="text-sm text-slate-400">$</span>
                      <input type="number" min="0" step="0.01" inputMode="decimal"
                        value={choices[f.id]?.nte ?? ''} onChange={(e) => setNte(f.id, e.target.value)}
                        placeholder={t.ntePlaceholder} data-testid={`pev-review-nte-${f.id}`}
                        className="w-full px-1 py-2 text-base outline-none" />
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{t.nteHelp}</p>
                  </div>
                )}
                <input value={choices[f.id]?.comment || ''} onChange={(e) => setComment(f.id, e.target.value)} placeholder={t.comment}
                  data-testid={`pev-review-comment-${f.id}`} className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              )}
            </section>
            );
          })}

          {(data?.findings || []).length > 0 && (
            <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.yourName} data-testid="pev-review-name" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-base" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.yourEmail} data-testid="pev-review-email" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-base" />
              </div>
            </div>
          )}
        </main>
      )}

      {!done && (data?.findings || []).length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
            <span className="text-sm font-medium text-slate-600" data-testid="pev-review-progress">{reviewedCount} {lang === 'es' ? 'de' : 'of'} {findings.length} {t.reviewed}</span>
            <button onClick={onSubmitClick} disabled={submitting || newSelections.length === 0} data-testid="pev-review-submit"
              className="inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-[#8FC93A] px-6 py-2.5 text-base font-bold text-[#0E2A47] disabled:opacity-50">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> {t.submitting}</> : <><FileText size={16} /> {t.submit}</>}
            </button>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" data-testid="pev-review-confirm" onClick={() => setConfirmOpen(false)}>
          <div className="w-full max-w-lg rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#0E2A47]">{t.confirmTitle}</h3>
            {unanswered > 0 && <p className="mt-2 text-sm text-slate-600" data-testid="pev-review-confirm-unanswered">{t.unansweredMsg(newSelections.length, findings.length, unanswered)}</p>}

            {(authSummary.fixedLines.length > 0) && (
              <div className="mt-3 rounded-xl border border-[#0E2A47]/15 bg-slate-50 p-4" data-testid="pev-review-auth-summary">
                <div className="text-xs font-bold uppercase tracking-wide text-[#0E2A47]">{t.authTitle}</div>
                <div className="mt-2 space-y-1 text-sm">
                  {authSummary.fixedLines.map((l, idx) => (
                    <div key={idx} className="flex justify-between gap-3">
                      <span className="min-w-0 truncate text-slate-700">{l.title}</span>
                      <span className="shrink-0 font-semibold text-[#0E2A47]">{t.fixedPrice}: {money(l.amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 border-t border-slate-200 pt-2 text-sm">
                  <div className="flex justify-between font-bold text-[#0E2A47]"><span>{t.fixedSubtotal}</span><span>{money(authSummary.fixed)}</span></div>
                </div>
              </div>
            )}

            {(authSummary.nteLines.length > 0) && (
              <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="pev-review-nte-summary">
                <div className="text-xs font-bold uppercase tracking-wide text-amber-700">{t.nteSummaryTitle}</div>
                <div className="mt-2 space-y-1 text-sm">
                  {authSummary.nteLines.map((l, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate text-slate-700">{l.title}</span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        {l.amount != null && <span className="font-semibold text-[#0E2A47]">{t.nte}: {money(l.amount)}</span>}
                        {l.review && <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800" data-testid="pev-review-nte-review-tag">{t.reviewTag}</span>}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-amber-700">{t.nteSummaryNote}</p>
              </div>
            )}

            {(authSummary.fixedLines.length > 0 || authSummary.nteLines.length > 0) && (
              <p className="mt-2 text-[11px] text-slate-500">{t.authNote}</p>
            )}

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button onClick={() => setConfirmOpen(false)} data-testid="pev-review-confirm-back"
                className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50">{unanswered > 0 ? t.reviewRemaining : t.goBack}</button>
              <button onClick={submit} disabled={submitting} data-testid="pev-review-confirm-submit"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#8FC93A] px-5 py-2.5 text-sm font-bold text-[#0E2A47] disabled:opacity-50">
                {submitting ? <><Loader2 size={15} className="animate-spin" /> {t.submitting}</> : (unanswered > 0 ? t.submitN(newSelections.length) : t.confirmAuthorize)}
              </button>
            </div>
          </div>
        </div>
      )}

      {lightbox && (() => {
        const p = lightbox.photos[lightbox.index];
        const multi = lightbox.photos.length > 1;
        return (
          <div className="fixed inset-0 z-[80] flex flex-col bg-black/90 p-3 sm:p-5" data-testid="pev-review-lightbox" onClick={() => setLightbox(null)}>
            <div className="mx-auto flex w-full max-w-5xl items-center justify-between pb-2" onClick={(e) => e.stopPropagation()}>
              <span className="text-sm font-medium text-white/80">
                {multi ? `${lightbox.index + 1} / ${lightbox.photos.length}` : ''}
                {p.kind === 'proposed' && <span className="ml-2 rounded bg-[#8FC93A] px-2 py-0.5 text-[10px] font-bold uppercase text-[#0E2A47]">{t.proposedConcept}</span>}
              </span>
              <button onClick={() => setLightbox(null)} data-testid="pev-review-lightbox-close"
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 px-3 py-2 text-sm font-semibold text-white hover:bg-white/25"><X size={16} /> {lang === 'es' ? 'Cerrar' : 'Close'}</button>
            </div>
            <div className="relative flex min-h-0 flex-1 items-center justify-center" onClick={(e) => e.stopPropagation()}>
              {multi && (
                <button onClick={() => lbNav(-1)} data-testid="pev-review-lightbox-prev"
                  className="absolute left-0 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30"><ChevronLeft size={22} /></button>
              )}
              <img src={photoSrc(p)} alt={p.caption || ''} className="max-h-full max-w-full rounded-lg object-contain" data-testid="pev-review-lightbox-img" />
              {multi && (
                <button onClick={() => lbNav(1)} data-testid="pev-review-lightbox-next"
                  className="absolute right-0 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30"><ChevronRight size={22} /></button>
              )}
            </div>
            {p.caption && <p className="mx-auto max-w-5xl pt-2 text-center text-sm text-white/80" onClick={(e) => e.stopPropagation()}>{p.caption}</p>}
          </div>
        );
      })()}
    </div>
  );
}
